import React, { useEffect, useState } from 'react';
import MessageBubble from './MessageBubble';
import './ChatWindow.css';
import OnlineStatusIndicator from '../onlinestatus/OnlineStatusIndicator';
import textEncryption from '../../encryption/textEncryption';
import textDecryption from '../../encryption/textDecryption';
import encryptImage from '../../encryption/imageEncryption';
import decryptImage from '../../encryption/imageDecryption';
import { rsaDecryptKey, rsaEncryptKey } from '../../encryption/aesKeyEncryption';
import rsaPrivateKeyDecryption from "../../encryption/rsaPrivateKeyDecryption";
import { API_BASE } from '../../config/api';


export default function ChatWindow({ friend, onClose, socket, onlineUsers }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [progress, setProgress] = useState(100);

  const [receivePopupData, setReceivePopupData] = useState(null);
  const [receiveProgress, setReceiveProgress] = useState(100);
  const [showReceivePopup, setShowReceivePopup] = useState(false);
  const [receiverRSAKey, setReceiverRSAKey] = useState({ e: '', n: '' });

  const friendId = friend ? String(friend.id || friend._id) : null;
  const isOnline = friendId ? onlineUsers.includes(friendId) : false;

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setSelectedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => setSelectedImage(null);

  // Load chat history
  useEffect(() => {
    if (!socket || !friend) return;

    socket.emit('loadChat', {
      user1: localStorage.getItem('userId'),
      user2: friend._id || friend.id
    });

    const handleChatHistory = async (historyMessages) => {
      const myId = localStorage.getItem('userId');

      const decryptedMessages = await Promise.all(historyMessages.map(async (msg) => {
        let decryptedText = '';
        let decryptedImg = null;

        try {
          decryptedText = msg.text ? textDecryption(msg.text, msg.key) : null;
        } catch {
          decryptedText = 'Error decrypting message';
        }

        if (msg.image) {
          try {
            decryptedImg = await decryptImage(msg.image);
          } catch {
            decryptedImg = null;
          }
        }

        return {
          text: decryptedText,
          image: decryptedImg,
          isOwn: msg.from === myId,
          username: msg.username || (msg.from === myId ? 'You' : friend.username),
          createdAt: msg.createdAt || new Date().toISOString()
        };
      }));

      setMessages(decryptedMessages);
    };

    socket.on('chatHistory', handleChatHistory);
    return () => socket.off('chatHistory', handleChatHistory);
  }, [socket, friend]);

  // Fetch friend's RSA key
  useEffect(() => {
    if (!friend) return;

    const receiverId = friend.id || friend._id;

    fetch(`${API_BASE}/api/rsa/publicKey/${receiverId}`)
      .then((res) => {
        if (!res.ok) throw new Error('RSA key not found');
        return res.json();
      })
      .then((data) => {
        if (data.e && data.n) setReceiverRSAKey({ e: data.e, n: data.n });
        else setReceiverRSAKey({ e: '', n: '' });
      })
      .catch((err) => {
        console.error('Failed to fetch RSA key:', err);
        setReceiverRSAKey({ e: '', n: '' });
      });
  }, [friend]);

  useEffect(() => {
    if (!socket || !friend) return;

    const handleReceive = async (data) => {
      const receiveStart = performance.now();

      const currentUserId = localStorage.getItem("userId");
      if (data.from === currentUserId) return;

      const openChatId = friend && (friend.id || friend._id);
      if (!openChatId || (data.from !== openChatId && data.to !== openChatId)) return;

      let plainPrivateKey;
      if (data.text && data.key) {
        const encryptedPrivateKey = localStorage.getItem("privateKey");
        if (!encryptedPrivateKey) return;
        try {
          const decryptedPrivateKeyStr = rsaPrivateKeyDecryption(encryptedPrivateKey);
          plainPrivateKey = JSON.parse(decryptedPrivateKeyStr);
        } catch (err) {
          console.error("Failed to process private key:", err);
          return;
        }
      }

      let plainAESKey = "";
      let decryptedText = "";
      let decryptedImg = null;
      const decryptStart = performance.now();
      try {
        if (data.text && data.key) {
          plainAESKey = rsaDecryptKey(data.key, plainPrivateKey.d, plainPrivateKey.n);
          decryptedText = plainAESKey
            ? textDecryption(data.text, plainAESKey)
            : "Error decrypting text";
        }
        if (data.image) {
          decryptedImg = await decryptImage(data.image);
        }
      } catch (err) {
        decryptedText = "Error decrypting text";
        decryptedImg = null;
      }
      const decryptEnd = performance.now();

      const receiveEnd = performance.now();

      setMessages((prev) => [
        ...prev,
        {
          text: decryptedText,
          image: decryptedImg,
          isOwn: false,
          username: data.username || (friend && friend.username) || "Friend",
          createdAt: data.createdAt || new Date().toISOString(),
        },
      ]);

      setReceivePopupData({
        ciphertext: data.text || "",
        key: data.key || "",
        plaintext: decryptedText || "",
        image: data.image || null,
        decryptedImage: decryptedImg || null,
        receiveTime: (receiveEnd - receiveStart).toFixed(2),
        decryptTime: (decryptEnd - decryptStart).toFixed(2),
      });

      setReceiveProgress(0);
      setShowReceivePopup(true);

      // Progress bar logic with auto-clear
      const start = Date.now();
      const duration = 50000;
      const interval = setInterval(() => {
        const elapsed = Date.now() - start;
        const newProgress = Math.min((elapsed / duration) * 100, 100);
        setReceiveProgress(newProgress);
        if (newProgress >= 100) {
          clearInterval(interval);
          setShowReceivePopup(false);
        }
      }, 50);
    };

    socket.on("receiveMessage", handleReceive);

    return () => {
      socket.off("receiveMessage", handleReceive);
    };
  }, [socket, friend]);


  // Send message
  const sendMessage = async () => {
    if (newMsg.trim() === "" && !selectedImage) return;

    const encryptionStart = performance.now();

    let encryptedText = "";
    let aesKey = "";
    let encryptedAESKey = "";
    let encryptedImg = null;

    // 🔒 Encrypt text only if present
    if (newMsg.trim()) {
      try {
        const result = textEncryption(newMsg.trim());
        encryptedText = result.ciphertext;
        aesKey = result.key;

        // RSA encrypt AES key only when text exists
        encryptedAESKey = rsaEncryptKey(aesKey, receiverRSAKey.e, receiverRSAKey.n);
      } catch (err) {
        console.error("Text encryption or RSA key encryption failed:", err);
      }
    }

    // 🖼️ Encrypt image if selected
    if (selectedImage) {
      try {
        encryptedImg = await encryptImage(selectedImage);
      } catch (err) {
        console.error("Image encryption failed:", err);
      }
    }

    const encryptionEnd = performance.now();
    const encryptionTime = (encryptionEnd - encryptionStart).toFixed(2);
    console.log(`Encryption time: ${encryptionTime} ms`);

    // Build message payload
    const msgData = {
      to: friend.id || friend._id,
      from: localStorage.getItem("userId"),
      text: encryptedText || null,
      key: encryptedAESKey || null, // only sent if text exists
      plainKey: aesKey || null,

      image: encryptedImg || null,
      username: localStorage.getItem("username"),
      sentAt: Date.now(),
      createdAt: new Date().toISOString(),
    };

    socket.emit("sendMessage", msgData);

    // Show immediately in UI (plaintext for sender)
    setMessages((prev) => [
      ...prev,
      {
        text: newMsg.trim() || "",
        image: selectedImage || null,
        isOwn: true,
        username: msgData.username,
        createdAt: msgData.createdAt,
      },
    ]);

    // Debug / popup info
    setPopupData({
      ciphertext: encryptedText || "",
      key: encryptedAESKey || "",
      plainKey: aesKey || "",
      plaintext: newMsg.trim() || "",
      image: encryptedImg || null,
      time: encryptionTime,
      plainimage: selectedImage || null,
    });

    // Progress bar logic
    setProgress(0);
    setShowPopup(true);
    const start = Date.now();
    const duration = 50000;
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);
      if (newProgress >= 100) {
        clearInterval(interval);
        setShowPopup(false);
      }
    }, 50);

    // Reset input
    setNewMsg("");
    setSelectedImage(null);
  };


  return (
    <div className="chat-window">
      {friend && (
        <div className="chat-header">
          <b>
            <img className="person-icon" src="images/personicon.png" alt="Person Icon" />
            <span>{friend.username}</span>
            <OnlineStatusIndicator isOnline={isOnline} />
          </b>
          <button className="close-button" onClick={onClose}>
            <img src="images/close-icon.png" className="close-icon" alt="Close Icon" />
          </button>
        </div>
      )}

      <div className="chat-body">
        {friend ? messages.map((msg, idx) => (
          <MessageBubble key={idx} message={msg} isOwn={msg.isOwn} />
        )) : <p className="chat-message">Add friends and chat</p>}
      </div>

      {friend && (
        <div className="chat-footer">
          <label htmlFor="file-upload" className="image-upload">
            <img src="images/image-input.png" alt="Upload" />
          </label>
          <input
            id="file-upload"
            type="file"
            accept="image/*"
            className="file-input"
            onChange={handleImageSelect}
          />
          <div className="input-wrapper">
            {selectedImage && (
              <div className="embedded-image">
                <img src={selectedImage} alt="Selected" />
                <span className="remove-image" onClick={removeImage}>×</span>
              </div>
            )}
            <input
              type="text"
              placeholder="Type a message..."
              value={newMsg}
              onChange={(e) => setNewMsg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              className="text-input"
            />
          </div>
          <button onClick={sendMessage} className="send-btn">Send</button>
        </div>
      )}

      {/* Sent Popup */}
      {showPopup && popupData && (
        <div className="popup-overlay">
          <div className="popup-box">
            <div className="popup-header">
              <span>✅ Sent Successfully</span>
              <img src="images/close-icon.png" alt="Close" className="popup-close" onClick={() => setShowPopup(false)} />
            </div>
            <div className="popup-progress">
              <div className="popup-bar" style={{ width: `${progress}%` }}></div>
            </div>
            <div className="popup-body">
              {popupData.plaintext && <div className="popup-row"><label>Plaintext:</label><div>{popupData.plaintext}</div></div>}
              {popupData.ciphertext && <div className="popup-row"><label>Cipher Text:</label><div>{popupData.ciphertext}</div></div>}
              {popupData.key && (
                <div className="popup-row">
                  <label>AES Plain Key:</label><div>{popupData.plainKey}</div>
                  <label>AES Encrypted Key:</label><div>{popupData.key}</div>
                </div>
              )}
              <div className="popup-row"><label>Encryption Time:</label><div>{popupData.time} ms</div></div>
              {popupData.image && (
                <div className="popup-image">
                  <label>Plain Image:</label>
                  <img src={popupData.plainimage} alt="Plain Preview" className="popup-image-preview" />
                  <label>Encrypted Image:</label>
                  <img src={popupData.image} alt="Encrypted Preview" className="popup-image-preview" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Receive Popup */}
      {showReceivePopup && receivePopupData && (
        <div className="popup-overlay">
          <div className="popup-box">
            <div className="popup-header">
              <span>📥 Received Successfully</span>
              <img src="images/close-icon.png" alt="Close" className="popup-close" onClick={() => setShowReceivePopup(false)} />
            </div>
            <div className="popup-progress">
              <div className="popup-bar" style={{ width: `${receiveProgress}%` }}></div>
            </div>
            <div className="popup-body">
              {receivePopupData.plaintext && <div className="popup-row"><label>Decrypted Text:</label><div>{receivePopupData.plaintext}</div></div>}
              {receivePopupData.ciphertext && <div className="popup-row"><label>Cipher Text:</label><div>{receivePopupData.ciphertext}</div></div>}
              {receivePopupData.key && <div className="popup-row"><label>AES Key:</label><div>{receivePopupData.key}</div></div>}
              <div className="popup-row"><label>Total Receive Time:</label><div>{receivePopupData.receiveTime} ms</div></div>
              <div className="popup-row"><label>Total Decrypt Time:</label><div>{receivePopupData.decryptTime} ms</div></div>
              {receivePopupData.image && (
                <div className="popup-image">
                  <label>Encrypted Image:</label>
                  <img src={receivePopupData.image} alt="Encrypted" className="popup-image-preview" />
                  <label>Decrypted Image:</label>
                  <img src={receivePopupData.decryptedImage} alt="Decrypted" className="popup-image-preview" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
